/** Validaciones de identificación y contacto usadas en Ecuador. */

/** Cédula ecuatoriana: 10 dígitos, provincia 01–24 o 30, tercer dígito hasta 6 y verificador módulo 10. El Registro Civil ya emite cédulas con tercer dígito 6 (por ejemplo, de bebés nacidos en los últimos años). */
export function cedulaValida(c: string): boolean {
  if (!/^\d{10}$/.test(c)) return false;
  const prov = Number(c.slice(0, 2));
  if (!((prov >= 1 && prov <= 24) || prov === 30)) return false;
  if (Number(c[2]) > 6) return false;
  let s = 0;
  for (let i = 0; i < 9; i++) {
    let d = Number(c[i]) * (i % 2 === 0 ? 2 : 1);
    if (d > 9) d -= 9;
    s += d;
  }
  return (10 - (s % 10)) % 10 === Number(c[9]);
}

/** RUC: 13 dígitos que terminan en 001 y con provincia válida (validación de formato). */
export function rucValido(r: string): boolean {
  if (!/^\d{13}$/.test(r) || !r.endsWith("001")) return false;
  const prov = Number(r.slice(0, 2));
  return (prov >= 1 && prov <= 24) || prov === 30;
}

/**
 * Número de otro país: empieza con «+» y el código del país (de 8 a 15 dígitos en total, norma internacional E.164).
 * Ejemplos: +34 612 345 678, +1 305 555 0123. Los de Ecuador (+593) se escriben 09XXXXXXXX o +593 9XXXXXXXX.
 */
export function celularExtranjero(t: string): boolean {
  const s = t.trim();
  if (!s.startsWith("+") || /[^\d\s().+-]/.test(s)) return false;
  const d = s.replace(/\D/g, "");
  return /^[1-9]\d{7,14}$/.test(d) && !d.startsWith("593");
}

/** Celular ecuatoriano (10 dígitos que empiezan con 09, o +593 9XXXXXXXX) o de otro país con «+» y su código. */
export function celularValido(t: string): boolean {
  const d = t.replace(/\D/g, "");
  if (/^09\d{8}$/.test(d)) return true;
  if (t.trim().startsWith("+") && /^5939\d{8}$/.test(d)) return true;
  return celularExtranjero(t);
}

/** Deja el celular listo para guardar: sin espacios; un número extranjero queda como +código y dígitos. */
export function limpiarCelular(t: string): string {
  const s = t.trim();
  return s.startsWith("+") ? "+" + s.replace(/\D/g, "") : s.replace(/\s/g, "");
}

/** Convierte el celular al formato internacional para WhatsApp (5939XXXXXXXX; los de otro país, sus dígitos con el código). */
export function celularInternacional(t: string): string | null {
  const d = t.replace(/\D/g, "");
  if (/^09\d{8}$/.test(d)) return "593" + d.slice(1);
  if (/^5939\d{8}$/.test(d)) return d;
  if (celularExtranjero(t)) return d;
  return null;
}

/** Celular (09XXXXXXXX) o teléfono fijo con código de provincia (02XXXXXXX); admite +593 y números de otros países con «+». */
export function telefonoValido(t: string): boolean {
  if (celularExtranjero(t)) return true;
  let d = t.replace(/\D/g, "");
  if (d.startsWith("593")) d = "0" + d.slice(3);
  return /^09\d{8}$/.test(d) || /^0[2-7]\d{7}$/.test(d);
}

export function correoValido(e: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(e);
}

/** Edad cumplida en años a una fecha dada (ISO yyyy-mm-dd). */
export function edad(nacimiento: string, hoy: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(nacimiento)) return null;
  const [y, m, d] = nacimiento.split("-").map(Number);
  const [Y, M, D] = hoy.split("-").map(Number);
  let e = Y - y;
  if (M < m || (M === m && D < d)) e--;
  return e >= 0 ? e : null;
}
